import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PremiumBadge } from '../PremiumGate';

function ExternalLinkField({
  canAddListLink,
  hasInactiveExternalLink,
  externalLinkUrl,
  externalLinkTitle,
  onChange,
  onClose,
  onShowReactivateModal,
}) {
  const navigate = useNavigate();

  // Inactive state: user had a link saved but premium expired
  if (hasInactiveExternalLink && !canAddListLink) {
    return (
      <div className="form-group premium-inactive-group">
        <label htmlFor="externalLinkUrl">
          External Link
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="inactive-lock-icon-label">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        </label>
        <div className="premium-inactive-content">
          <div className="premium-inactive-indicator">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
            </svg>
            <span>Link saved</span>
          </div>
          <p className="premium-inactive-text">Your link is saved but not visible to others.</p>
          <button
            type="button"
            className="btn-reactivate-small"
            onClick={onShowReactivateModal}
          >
            Reactivate Premium
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`form-group ${!canAddListLink ? 'premium-locked-group' : ''}`}>
      <label htmlFor="externalLinkUrl">
        External Link
        {!canAddListLink && <PremiumBadge small />}
      </label>
      {canAddListLink ? (
        <>
          <input
            type="url"
            id="externalLinkUrl"
            name="externalLinkUrl"
            value={externalLinkUrl}
            onChange={onChange}
            placeholder="https://..."
          />
          <input
            type="text"
            id="externalLinkTitle"
            name="externalLinkTitle"
            value={externalLinkTitle}
            onChange={onChange}
            placeholder="Link title (optional)"
            className="input-small"
          />
          <p className="field-hint">Add a link viewers can click on (e.g., your blog post)</p>
        </>
      ) : (
        <div className="premium-locked-content">
          <p className="premium-locked-text">Add a custom link to your list</p>
          <button
            type="button"
            className="btn-upgrade-small"
            onClick={() => {
              onClose();
              navigate('/settings/premium');
            }}
          >
            Upgrade to Premium
          </button>
        </div>
      )}
    </div>
  );
}

function CategoryOverrideField({
  canOverrideListMeta,
  categoryOverride,
  availableCategories,
  onChange,
  onClose,
}) {
  const navigate = useNavigate();

  return (
    <div className={`form-group ${!canOverrideListMeta ? 'premium-locked-group' : ''}`}>
      <label htmlFor="categoryOverride">
        Category Override
        {!canOverrideListMeta && <PremiumBadge small />}
      </label>
      {canOverrideListMeta ? (
        <>
          <select
            id="categoryOverride"
            name="categoryOverride"
            value={categoryOverride}
            onChange={onChange}
            disabled={availableCategories.length === 0}
          >
            <option value="">Auto-detect from places</option>
            {availableCategories.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          {availableCategories.length === 0 ? (
            <p className="field-hint field-hint-warning">
              Add places with a consistent category (50%+ matching) to enable override
            </p>
          ) : (
            <p className="field-hint">
              Override the auto-detected category. Only categories with 50%+ of places are available.
            </p>
          )}
        </>
      ) : (
        <div className="premium-locked-content">
          <p className="premium-locked-text">Override the auto-detected list category</p>
          <button
            type="button"
            className="btn-upgrade-small"
            onClick={() => {
              onClose();
              navigate('/settings/premium');
            }}
          >
            Upgrade to Premium
          </button>
        </div>
      )}
    </div>
  );
}

function LocationOverrideField({
  canOverrideListMeta,
  locationOverride,
  availableLocations,
  onChange,
  onClose,
}) {
  const navigate = useNavigate();

  // Build a unique key for the current locationOverride to match against options
  const getCurrentValue = () => {
    if (!locationOverride) return '';
    if (locationOverride.city) {
      return `city:${locationOverride.city},${locationOverride.stateCode || locationOverride.state}`;
    }
    if (locationOverride.state || locationOverride.stateCode) {
      return `state:${locationOverride.stateCode || locationOverride.state}`;
    }
    if (locationOverride.country || locationOverride.countryCode) {
      return `country:${locationOverride.countryCode || locationOverride.country}`;
    }
    return '';
  };

  const handleLocationChange = (e) => {
    const val = e.target.value;
    if (!val) {
      onChange({ locationOverride: null });
      return;
    }

    // Find the selected location object from availableLocations
    const selectedLoc = availableLocations.find(loc => {
      if (loc.type === 'city') {
        return val === `city:${loc.city},${loc.stateCode || loc.state}`;
      }
      if (loc.type === 'state') {
        return val === `state:${loc.stateCode || loc.state}`;
      }
      if (loc.type === 'country') {
        return val === `country:${loc.countryCode || loc.country}`;
      }
      return false;
    });

    if (selectedLoc) {
      onChange({
        locationOverride: {
          city: selectedLoc.city,
          state: selectedLoc.state,
          stateCode: selectedLoc.stateCode,
          country: selectedLoc.country,
          countryCode: selectedLoc.countryCode,
        }
      });
    }
  };

  // Group locations by type for display
  const cityLocations = availableLocations.filter(loc => loc.type === 'city');
  const stateLocations = availableLocations.filter(loc => loc.type === 'state');
  const countryLocations = availableLocations.filter(loc => loc.type === 'country');

  return (
    <div className={`form-group ${!canOverrideListMeta ? 'premium-locked-group' : ''}`}>
      <label htmlFor="locationOverride">
        Location Override
        {!canOverrideListMeta && <PremiumBadge small />}
      </label>
      {canOverrideListMeta ? (
        <>
          <select
            id="locationOverride"
            value={getCurrentValue()}
            onChange={handleLocationChange}
            disabled={availableLocations.length === 0}
          >
            <option value="">Auto-detect from places</option>
            {cityLocations.length > 0 && (
              <optgroup label="City">
                {cityLocations.map((loc) => (
                  <option
                    key={`city:${loc.city},${loc.stateCode || loc.state}`}
                    value={`city:${loc.city},${loc.stateCode || loc.state}`}
                  >
                    {loc.display}
                  </option>
                ))}
              </optgroup>
            )}
            {stateLocations.length > 0 && (
              <optgroup label="State">
                {stateLocations.map((loc) => (
                  <option
                    key={`state:${loc.stateCode || loc.state}`}
                    value={`state:${loc.stateCode || loc.state}`}
                  >
                    {loc.display}
                  </option>
                ))}
              </optgroup>
            )}
            {countryLocations.length > 0 && (
              <optgroup label="Country">
                {countryLocations.map((loc) => (
                  <option
                    key={`country:${loc.countryCode || loc.country}`}
                    value={`country:${loc.countryCode || loc.country}`}
                  >
                    {loc.display}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          {availableLocations.length === 0 ? (
            <p className="field-hint field-hint-warning">
              Add places in a consistent location (50%+ matching) to enable override
            </p>
          ) : (
            <p className="field-hint">
              Override the auto-detected location. Only locations with 50%+ of places are available.
            </p>
          )}
        </>
      ) : (
        <div className="premium-locked-content">
          <p className="premium-locked-text">Override the auto-detected list location</p>
          <button
            type="button"
            className="btn-upgrade-small"
            onClick={() => {
              onClose();
              navigate('/settings/premium');
            }}
          >
            Upgrade to Premium
          </button>
        </div>
      )}
    </div>
  );
}

export { ExternalLinkField, CategoryOverrideField, LocationOverrideField };
